import React, { useState, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { FaArrowLeft, FaArrowRight, FaUser, FaUsers, FaRulerVertical, FaGraduationCap, FaFileAlt, FaHeart, FaCamera, FaCheckCircle, FaCloudUploadAlt, FaTimes, FaEye, FaEyeSlash, FaCheck } from 'react-icons/fa';
import castes from '../data/castes';
import cities from '../data/cities';
import qualifications from '../data/qualifications';

/* ------------------------------------------------------------------ */
/*  Reference data                                                     */
/* ------------------------------------------------------------------ */
const rashis = ['मेष (Aries)', 'वृषभ (Taurus)', 'मिथुन (Gemini)', 'कर्क (Cancer)', 'सिंह (Leo)', 'कन्या (Virgo)', 'तुला (Libra)', 'वृश्चिक (Scorpio)', 'धनु (Sagittarius)', 'मकर (Capricorn)', 'कुंभ (Aquarius)', 'मीन (Pisces)'];

const gothras = ['Bharadwaj', 'Kashyap', 'Vatsa', 'Gautam', 'Jamadagni', 'Vashistha', 'Vishwamitra', 'Atri', 'Agastya', 'Angiras', 'Pulastya', 'Pulaha', 'Kratu', 'Marichi', 'Daksha', 'Other'];

const occupations = ['Government Job', 'Private Job', 'Business', 'Self-Employed', 'Doctor', 'Engineer', 'Teacher / Professor', 'Lawyer', 'CA / CS', 'Farmer', 'IT Professional', 'Banking', 'Defence / Military', 'Police', 'Nursing', 'Student', 'Not Working', 'Other'];

const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const complexions = ['Fair', 'Very Fair', 'Wheatish', 'Dark'];
const motherTongues = ['Marathi', 'Hindi', 'English', 'Kannada', 'Telugu', 'Other'];

const STEPS = [
  { icon: <FaUser />, label: 'वैयक्तिक', eng: 'Personal' },
  { icon: <FaUsers />, label: 'समुदाय', eng: 'Community' },
  { icon: <FaRulerVertical />, label: 'शारीरिक', eng: 'Physical' },
  { icon: <FaGraduationCap />, label: 'शिक्षण', eng: 'Education' },
  { icon: <FaFileAlt />, label: 'माझ्याबद्दल', eng: 'About' },
  { icon: <FaHeart />, label: 'पसंती', eng: 'Preference' },
  { icon: <FaCamera />, label: 'फोटो', eng: 'Photos' },
  { icon: <FaCheckCircle />, label: 'पुनरावलोकन', eng: 'Review' },
];

/* ------------------------------------------------------------------ */
/*  Step Indicator                                                     */
/* ------------------------------------------------------------------ */
const StepIndicator = ({ currentStep }) => (
  <div className="w-full overflow-x-auto pb-2">
    <div className="flex items-center justify-between min-w-[600px] px-4">
      {STEPS.map((s, i) => {
        const isActive = i === currentStep;
        const isDone = i < currentStep;
        return (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center gap-1">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                  isDone
                    ? 'bg-green-500 text-white'
                    : isActive
                    ? 'bg-gradient-to-br from-[#DC143C] to-[#8B0000] text-white shadow-lg scale-110'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {isDone ? <FaCheck /> : s.icon}
              </div>
              <span className={`text-xs font-['Noto_Sans_Devanagari'] ${isActive ? 'text-[#8B0000] font-bold' : 'text-gray-500'}`}>
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mx-1 ${i < currentStep ? 'bg-green-500' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Reusable form components                                           */
/* ------------------------------------------------------------------ */
const FormField = ({ label, error, children, required }) => (
  <div className="mb-4">
    <label className="block text-sm font-semibold text-[#8B0000] mb-1.5 font-['Noto_Sans_Devanagari']">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
);

const Input = ({ type = 'text', value, onChange, placeholder, ...props }) => (
  <input
    type={type}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-[#DC143C] focus:ring-2 focus:ring-[#DC143C]/10 transition-all duration-200"
    {...props}
  />
);

const Select = ({ value, onChange, options, placeholder, ...props }) => (
  <select
    value={value}
    onChange={onChange}
    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-[#DC143C] focus:ring-2 focus:ring-[#DC143C]/10 transition-all duration-200 appearance-none"
    {...props}
  >
    <option value="">{placeholder || 'Select...'}</option>
    {options.map((o) => (
      <option key={typeof o === 'string' ? o : o.value} value={typeof o === 'string' ? o : o.value}>
        {typeof o === 'string' ? o : o.label}
      </option>
    ))}
  </select>
);

const TextArea = ({ value, onChange, placeholder, maxLength, ...props }) => (
  <div className="relative">
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      maxLength={maxLength}
      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-[#DC143C] focus:ring-2 focus:ring-[#DC143C]/10 transition-all duration-200 min-h-[100px] resize-y"
      {...props}
    />
    {maxLength && (
      <span className="absolute bottom-2 right-3 text-xs text-gray-400">{(value || '').length}/{maxLength}</span>
    )}
  </div>
);

/* ------------------------------------------------------------------ */
/*  Photo Upload Component                                             */
/* ------------------------------------------------------------------ */
const PhotoUpload = ({ label, files, setFiles, multiple = false, maxFiles = 1 }) => {
  const inputRef = useRef(null);

  const handleChange = (e) => {
    const selected = Array.from(e.target.files);
    if (multiple) {
      const combined = [...(files || []), ...selected].slice(0, maxFiles);
      setFiles(combined);
    } else {
      setFiles(selected[0] || null);
    }
    e.target.value = '';
  };

  const removeFile = (index) => {
    if (multiple) {
      setFiles((files || []).filter((_, i) => i !== index));
    } else {
      setFiles(null);
    }
  };

  const fileList = multiple ? files || [] : files ? [files] : [];

  return (
    <div className="mb-4">
      <label className="block text-sm font-semibold text-[#8B0000] mb-1.5 font-['Noto_Sans_Devanagari']">{label}</label>
      <div
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-[#B8860B]/40 rounded-xl p-6 text-center cursor-pointer hover:border-[#DC143C] hover:bg-[#DC143C]/5 transition-all duration-200"
      >
        <FaCloudUploadAlt className="mx-auto text-3xl text-[#B8860B] mb-2" />
        <p className="text-sm text-gray-500">Click to upload {multiple && `(max ${maxFiles})`}</p>
        <input ref={inputRef} type="file" accept="image/*" multiple={multiple} onChange={handleChange} className="hidden" />
      </div>
      {fileList.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-3">
          {fileList.map((f, i) => (
            <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border-2 border-[#FFD700] group">
              <img src={URL.createObjectURL(f)} alt="preview" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl-lg opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <FaTimes size={10} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Multi-select checkbox                                              */
/* ------------------------------------------------------------------ */
const MultiSelect = ({ options, selected, onChange, columns = 3 }) => (
  <div className={`grid grid-cols-2 md:grid-cols-${columns} gap-2 max-h-48 overflow-y-auto p-2 border border-gray-200 rounded-lg bg-white`}>
    {options.map((opt) => {
      const val = typeof opt === 'string' ? opt : opt.value;
      const label = typeof opt === 'string' ? opt : opt.label;
      const checked = (selected || []).includes(val);
      return (
        <label key={val} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[#FFF8F0] p-1 rounded">
          <input
            type="checkbox"
            checked={checked}
            onChange={() => {
              if (checked) onChange((selected || []).filter((v) => v !== val));
              else onChange([...(selected || []), val]);
            }}
            className="accent-[#DC143C]"
          />
          <span className="truncate">{label}</span>
        </label>
      );
    })}
  </div>
);

/* ================================================================== */
/*  REGISTER PAGE                                                      */
/* ================================================================== */
const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [agreedTerms, setAgreedTerms] = useState(false);

  const [form, setForm] = useState({
    // Step 1
    first_name: '', middle_name: '', last_name: '', gender: '', dob: '',
    mobile: '', email: '', password: '', confirm_password: '',
    // Step 2
    religion: 'Hindu', caste: '', sub_caste: '', rashi: '', gothra: '',
    city: '', state: 'Maharashtra', mother_tongue: 'Marathi',
    // Step 3
    blood_group: '', complexion: '', height_cm: '', weight_kg: '',
    // Step 4
    qualification: '', occupation: '', annual_income: '',
    // Step 5
    bio: '', permanent_address: '', current_address: '', same_address: false,
    // Step 6
    min_age: '', max_age: '', preferred_gender: '',
    preferred_castes: [], preferred_cities: [], preferred_qualifications: [],
    min_height: '', max_height: '', preferred_complexion: '', min_income: '',
    preferred_occupation: '', other_preferences: '',
    // Step 7
    profile_photo: null, additional_photos: [], aadhaar_photo: null, pan_photo: null,
  });

  const set = useCallback((field, value) => {
    setForm((p) => {
      const next = { ...p, [field]: value };
      // Auto-set preferred_gender
      if (field === 'gender') {
        next.preferred_gender = value === 'Male' ? 'Female' : value === 'Female' ? 'Male' : '';
      }
      // Same-as-permanent
      if (field === 'same_address' && value) {
        next.current_address = next.permanent_address;
      }
      if (field === 'permanent_address' && next.same_address) {
        next.current_address = value;
      }
      return next;
    });
    setErrors((p) => ({ ...p, [field]: undefined }));
  }, []);

  /* ---- Height helper ---- */
  const heightToFtIn = (cm) => {
    if (!cm) return '';
    const inches = cm / 2.54;
    const ft = Math.floor(inches / 12);
    const inRemainder = Math.round(inches % 12);
    return `${ft}'${inRemainder}"`;
  };

  /* ---- Validators per step ---- */
  const validateStep = () => {
    const e = {};
    if (step === 0) {
      if (!form.first_name.trim()) e.first_name = 'First name is required';
      if (!form.last_name.trim()) e.last_name = 'Last name is required';
      if (!form.gender) e.gender = 'Gender is required';
      if (!form.dob) e.dob = 'Date of birth is required';
      if (!form.mobile.trim() || !/^\d{10}$/.test(form.mobile)) e.mobile = 'Valid 10-digit mobile required';
      if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email required';
      if (!form.password || form.password.length < 6) e.password = 'Password must be at least 6 characters';
      if (form.password !== form.confirm_password) e.confirm_password = 'Passwords do not match';
    } else if (step === 1) {
      if (!form.caste) e.caste = 'Caste is required';
      if (!form.city) e.city = 'City is required';
    } else if (step === 2) {
      if (!form.height_cm) e.height_cm = 'Height is required';
    } else if (step === 3) {
      if (!form.qualification) e.qualification = 'Qualification is required';
      if (!form.occupation) e.occupation = 'Occupation is required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const nextStep = () => {
    if (validateStep()) setStep((s) => Math.min(s + 1, 7));
  };

  const prevStep = () => setStep((s) => Math.max(s - 1, 0));

  const goToStep = (s) => setStep(s);

  /* ---- Submit ---- */
  const handleSubmit = async () => {
    if (!agreedTerms) {
      toast.error('Please accept terms and conditions');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      // Append text fields
      const textFields = [
        'first_name', 'middle_name', 'last_name', 'gender', 'dob', 'mobile', 'email', 'password',
        'religion', 'caste', 'sub_caste', 'rashi', 'gothra', 'city', 'state', 'mother_tongue',
        'blood_group', 'complexion', 'height_cm', 'weight_kg',
        'qualification', 'occupation', 'annual_income',
        'bio', 'permanent_address', 'current_address',
        'min_age', 'max_age', 'preferred_gender', 'preferred_complexion',
        'min_height', 'max_height', 'min_income', 'preferred_occupation', 'other_preferences',
      ];
      textFields.forEach((k) => { if (form[k] !== undefined && form[k] !== '') formData.append(k, form[k]); });

      // Array fields
      ['preferred_castes', 'preferred_cities', 'preferred_qualifications'].forEach((k) => {
        (form[k] || []).forEach((v) => formData.append(k, v));
      });

      // Photos
      if (form.profile_photo) formData.append('profile_photo', form.profile_photo);
      (form.additional_photos || []).forEach((f) => formData.append('additional_photos', f));
      if (form.aadhaar_photo) formData.append('aadhaar_photo', form.aadhaar_photo);
      if (form.pan_photo) formData.append('pan_photo', form.pan_photo);

      await register(formData);
      toast.success('नोंदणी यशस्वी! Welcome to Vivah Arambh!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /* ================================================================ */
  /*  Step renderers                                                    */
  /* ================================================================ */

  const renderStep0 = () => (
    <div className="space-y-1">
      <h3 className="text-xl font-['Noto_Sans_Devanagari'] font-bold text-[#8B0000] mb-4">वैयक्तिक माहिती</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4">
        <FormField label="पहिले नाव (First Name)" required error={errors.first_name}>
          <Input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} placeholder="First Name" />
        </FormField>
        <FormField label="मधले नाव (Middle Name)">
          <Input value={form.middle_name} onChange={(e) => set('middle_name', e.target.value)} placeholder="Middle Name" />
        </FormField>
        <FormField label="आडनाव (Last Name)" required error={errors.last_name}>
          <Input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} placeholder="Last Name" />
        </FormField>
      </div>

      <FormField label="लिंग (Gender)" required error={errors.gender}>
        <div className="flex gap-6">
          {['Male', 'Female'].map((g) => (
            <label key={g} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="gender"
                checked={form.gender === g}
                onChange={() => set('gender', g)}
                className="accent-[#DC143C] w-4 h-4"
              />
              <span>{g === 'Male' ? 'पुरुष (Male)' : 'स्त्री (Female)'}</span>
            </label>
          ))}
        </div>
      </FormField>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
        <FormField label="जन्मतारीख (Date of Birth)" required error={errors.dob}>
          <Input type="date" value={form.dob} onChange={(e) => set('dob', e.target.value)} max={new Date().toISOString().split('T')[0]} />
        </FormField>
        <FormField label="मोबाईल नंबर (Mobile)" required error={errors.mobile}>
          <div className="flex">
            <span className="px-3 py-2.5 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg text-sm text-gray-500">+91</span>
            <input
              type="tel"
              value={form.mobile}
              onChange={(e) => set('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))}
              placeholder="9876543210"
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-r-lg bg-white focus:outline-none focus:border-[#DC143C] focus:ring-2 focus:ring-[#DC143C]/10"
            />
          </div>
        </FormField>
      </div>

      <FormField label="ईमेल (Email)" required error={errors.email}>
        <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="email@example.com" />
      </FormField>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
        <FormField label="पासवर्ड (Password)" required error={errors.password}>
          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={(e) => set('password', e.target.value)}
              placeholder="Min 6 characters"
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
        </FormField>
        <FormField label="पासवर्ड पुन्हा टाका (Confirm)" required error={errors.confirm_password}>
          <Input type="password" value={form.confirm_password} onChange={(e) => set('confirm_password', e.target.value)} placeholder="Re-enter password" />
        </FormField>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-1">
      <h3 className="text-xl font-['Noto_Sans_Devanagari'] font-bold text-[#8B0000] mb-4">समुदाय माहिती</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
        <FormField label="धर्म (Religion)">
          <Select value={form.religion} onChange={(e) => set('religion', e.target.value)} options={['Hindu', 'Jain', 'Buddhist', 'Sikh', 'Other']} />
        </FormField>
        <FormField label="जात (Caste)" required error={errors.caste}>
          <Select value={form.caste} onChange={(e) => set('caste', e.target.value)} options={castes} placeholder="Select Caste" />
        </FormField>
      </div>
      <FormField label="पोटजात (Sub-Caste)">
        <Input value={form.sub_caste} onChange={(e) => set('sub_caste', e.target.value)} placeholder="Sub-Caste (optional)" />
      </FormField>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
        <FormField label="राशी (Rashi)">
          <Select value={form.rashi} onChange={(e) => set('rashi', e.target.value)} options={rashis} placeholder="Select Rashi" />
        </FormField>
        <FormField label="गोत्र (Gothra)">
          <Select value={form.gothra} onChange={(e) => set('gothra', e.target.value)} options={gothras} placeholder="Select Gothra" />
        </FormField>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4">
        <FormField label="शहर (City)" required error={errors.city}>
          <Select value={form.city} onChange={(e) => set('city', e.target.value)} options={cities} placeholder="Select City" />
        </FormField>
        <FormField label="राज्य (State)">
          <Input value={form.state} onChange={(e) => set('state', e.target.value)} placeholder="State" />
        </FormField>
        <FormField label="मातृभाषा (Mother Tongue)">
          <Select value={form.mother_tongue} onChange={(e) => set('mother_tongue', e.target.value)} options={motherTongues} />
        </FormField>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-1">
      <h3 className="text-xl font-['Noto_Sans_Devanagari'] font-bold text-[#8B0000] mb-4">शारीरिक माहिती</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
        <FormField label="रक्तगट (Blood Group)">
          <Select value={form.blood_group} onChange={(e) => set('blood_group', e.target.value)} options={bloodGroups} placeholder="Select Blood Group" />
        </FormField>
        <FormField label="वर्ण (Complexion)">
          <Select value={form.complexion} onChange={(e) => set('complexion', e.target.value)} options={complexions} placeholder="Select Complexion" />
        </FormField>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
        <FormField label="उंची (Height in cm)" required error={errors.height_cm}>
          <div>
            <Input
              type="number"
              value={form.height_cm}
              onChange={(e) => set('height_cm', e.target.value)}
              placeholder="e.g. 170"
              min="120"
              max="220"
            />
            {form.height_cm && (
              <p className="text-xs text-gray-500 mt-1">= {heightToFtIn(form.height_cm)}</p>
            )}
          </div>
        </FormField>
        <FormField label="वजन (Weight in kg)">
          <Input type="number" value={form.weight_kg} onChange={(e) => set('weight_kg', e.target.value)} placeholder="e.g. 65" min="30" max="200" />
        </FormField>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-1">
      <h3 className="text-xl font-['Noto_Sans_Devanagari'] font-bold text-[#8B0000] mb-4">शिक्षण आणि करिअर</h3>
      <FormField label="शिक्षण (Qualification)" required error={errors.qualification}>
        <Select value={form.qualification} onChange={(e) => set('qualification', e.target.value)} options={qualifications} placeholder="Select Qualification" />
      </FormField>
      <FormField label="व्यवसाय (Occupation)" required error={errors.occupation}>
        <Select value={form.occupation} onChange={(e) => set('occupation', e.target.value)} options={occupations} placeholder="Select Occupation" />
      </FormField>
      <FormField label="वार्षिक उत्पन्न (Annual Income)">
        <div className="flex">
          <span className="px-3 py-2.5 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg text-sm text-gray-500">₹</span>
          <input
            type="number"
            value={form.annual_income}
            onChange={(e) => set('annual_income', e.target.value)}
            placeholder="e.g. 500000"
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-r-lg bg-white focus:outline-none focus:border-[#DC143C] focus:ring-2 focus:ring-[#DC143C]/10"
          />
        </div>
      </FormField>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-1">
      <h3 className="text-xl font-['Noto_Sans_Devanagari'] font-bold text-[#8B0000] mb-4">माझ्याबद्दल</h3>
      <FormField label="स्वतःबद्दल (About Me)">
        <TextArea value={form.bio} onChange={(e) => set('bio', e.target.value)} placeholder="Tell us about yourself..." maxLength={500} />
      </FormField>
      <FormField label="कायमचा पत्ता (Permanent Address)">
        <TextArea value={form.permanent_address} onChange={(e) => set('permanent_address', e.target.value)} placeholder="Permanent Address" />
      </FormField>
      <label className="flex items-center gap-2 mb-3 cursor-pointer">
        <input type="checkbox" checked={form.same_address} onChange={(e) => set('same_address', e.target.checked)} className="accent-[#DC143C] w-4 h-4" />
        <span className="text-sm">Current address same as permanent</span>
      </label>
      {!form.same_address && (
        <FormField label="सध्याचा पत्ता (Current Address)">
          <TextArea value={form.current_address} onChange={(e) => set('current_address', e.target.value)} placeholder="Current Address" />
        </FormField>
      )}
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-1">
      <h3 className="text-xl font-['Noto_Sans_Devanagari'] font-bold text-[#8B0000] mb-4">जोडीदार पसंती</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4">
        <FormField label="किमान वय (Min Age)">
          <Input type="number" value={form.min_age} onChange={(e) => set('min_age', e.target.value)} placeholder="18" min="18" max="70" />
        </FormField>
        <FormField label="कमाल वय (Max Age)">
          <Input type="number" value={form.max_age} onChange={(e) => set('max_age', e.target.value)} placeholder="40" min="18" max="70" />
        </FormField>
        <FormField label="पसंतीचे लिंग (Preferred Gender)">
          <Input value={form.preferred_gender} readOnly className="bg-gray-50" />
        </FormField>
      </div>

      <FormField label="पसंतीच्या जाती (Preferred Castes)">
        <MultiSelect options={castes} selected={form.preferred_castes} onChange={(v) => set('preferred_castes', v)} />
      </FormField>

      <FormField label="पसंतीची शहरे (Preferred Cities)">
        <MultiSelect options={cities} selected={form.preferred_cities} onChange={(v) => set('preferred_cities', v)} />
      </FormField>

      <FormField label="पसंतीचे शिक्षण (Preferred Qualifications)">
        <MultiSelect options={qualifications} selected={form.preferred_qualifications} onChange={(v) => set('preferred_qualifications', v)} />
      </FormField>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
        <FormField label="किमान उंची cm (Min Height)">
          <Input type="number" value={form.min_height} onChange={(e) => set('min_height', e.target.value)} placeholder="150" />
        </FormField>
        <FormField label="कमाल उंची cm (Max Height)">
          <Input type="number" value={form.max_height} onChange={(e) => set('max_height', e.target.value)} placeholder="190" />
        </FormField>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4">
        <FormField label="पसंतीचा वर्ण (Complexion)">
          <Select value={form.preferred_complexion} onChange={(e) => set('preferred_complexion', e.target.value)} options={['Any', ...complexions]} placeholder="Select" />
        </FormField>
        <FormField label="किमान उत्पन्न (Min Income)">
          <div className="flex">
            <span className="px-3 py-2.5 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg text-sm text-gray-500">₹</span>
            <input type="number" value={form.min_income} onChange={(e) => set('min_income', e.target.value)} placeholder="e.g. 300000" className="flex-1 px-4 py-2.5 border border-gray-300 rounded-r-lg bg-white focus:outline-none focus:border-[#DC143C] focus:ring-2 focus:ring-[#DC143C]/10" />
          </div>
        </FormField>
        <FormField label="पसंतीचा व्यवसाय (Occupation)">
          <Select value={form.preferred_occupation} onChange={(e) => set('preferred_occupation', e.target.value)} options={['Any', ...occupations]} placeholder="Select" />
        </FormField>
      </div>

      <FormField label="इतर पसंती (Other Preferences)">
        <TextArea value={form.other_preferences} onChange={(e) => set('other_preferences', e.target.value)} placeholder="Any other preferences..." maxLength={300} />
      </FormField>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-1">
      <h3 className="text-xl font-['Noto_Sans_Devanagari'] font-bold text-[#8B0000] mb-4">फोटो अपलोड</h3>
      <PhotoUpload
        label="प्रोफाइल फोटो (Profile Photo) *"
        files={form.profile_photo}
        setFiles={(f) => set('profile_photo', f)}
      />
      <PhotoUpload
        label="अतिरिक्त फोटो (Additional Photos - up to 5)"
        files={form.additional_photos}
        setFiles={(f) => set('additional_photos', f)}
        multiple
        maxFiles={5}
      />
      <PhotoUpload
        label="आधार कार्ड फोटो (Aadhaar - optional)"
        files={form.aadhaar_photo}
        setFiles={(f) => set('aadhaar_photo', f)}
      />
      <PhotoUpload
        label="पॅन कार्ड फोटो (PAN - optional)"
        files={form.pan_photo}
        setFiles={(f) => set('pan_photo', f)}
      />
    </div>
  );

  const ReviewSection = ({ title, data, editStep }) => (
    <div className="bg-white rounded-xl p-5 border border-[#FFD700]/30 mb-4">
      <div className="flex justify-between items-center mb-3">
        <h4 className="font-['Noto_Sans_Devanagari'] font-bold text-[#8B0000]">{title}</h4>
        <button type="button" onClick={() => goToStep(editStep)} className="text-sm text-[#DC143C] hover:underline">Edit</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
        {Object.entries(data).map(([k, v]) => {
          if (!v || (Array.isArray(v) && v.length === 0)) return null;
          const displayVal = Array.isArray(v) ? v.join(', ') : String(v);
          return (
            <div key={k} className="flex py-1 text-sm">
              <span className="text-gray-500 w-40 flex-shrink-0">{k}:</span>
              <span className="text-gray-800 font-medium">{displayVal}</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderStep7 = () => (
    <div className="space-y-2">
      <h3 className="text-xl font-['Noto_Sans_Devanagari'] font-bold text-[#8B0000] mb-4">पुनरावलोकन आणि सबमिट</h3>

      <ReviewSection title="वैयक्तिक माहिती" editStep={0} data={{
        'Name': `${form.first_name} ${form.middle_name} ${form.last_name}`.trim(),
        'Gender': form.gender, 'DOB': form.dob, 'Mobile': form.mobile, 'Email': form.email,
      }} />

      <ReviewSection title="समुदाय माहिती" editStep={1} data={{
        'Religion': form.religion, 'Caste': form.caste, 'Sub-Caste': form.sub_caste,
        'Rashi': form.rashi, 'Gothra': form.gothra, 'City': form.city, 'State': form.state, 'Mother Tongue': form.mother_tongue,
      }} />

      <ReviewSection title="शारीरिक माहिती" editStep={2} data={{
        'Blood Group': form.blood_group, 'Complexion': form.complexion,
        'Height': form.height_cm ? `${form.height_cm} cm (${heightToFtIn(form.height_cm)})` : '',
        'Weight': form.weight_kg ? `${form.weight_kg} kg` : '',
      }} />

      <ReviewSection title="शिक्षण आणि करिअर" editStep={3} data={{
        'Qualification': form.qualification, 'Occupation': form.occupation,
        'Annual Income': form.annual_income ? `₹${Number(form.annual_income).toLocaleString()}` : '',
      }} />

      <ReviewSection title="माझ्याबद्दल" editStep={4} data={{
        'Bio': form.bio, 'Permanent Address': form.permanent_address, 'Current Address': form.current_address,
      }} />

      <ReviewSection title="जोडीदार पसंती" editStep={5} data={{
        'Age Range': (form.min_age || form.max_age) ? `${form.min_age || '?'} - ${form.max_age || '?'}` : '',
        'Preferred Gender': form.preferred_gender,
        'Preferred Castes': form.preferred_castes,
        'Preferred Cities': form.preferred_cities,
        'Height Range': (form.min_height || form.max_height) ? `${form.min_height || '?'} - ${form.max_height || '?'} cm` : '',
      }} />

      <div className="bg-white rounded-xl p-5 border border-[#FFD700]/30 mb-4">
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-['Noto_Sans_Devanagari'] font-bold text-[#8B0000]">फोटो</h4>
          <button type="button" onClick={() => goToStep(6)} className="text-sm text-[#DC143C] hover:underline">Edit</button>
        </div>
        <div className="flex gap-3 flex-wrap">
          {form.profile_photo && (
            <div className="w-20 h-20 rounded-lg overflow-hidden border-2 border-[#FFD700]">
              <img src={URL.createObjectURL(form.profile_photo)} alt="profile" className="w-full h-full object-cover" />
            </div>
          )}
          {(form.additional_photos || []).map((f, i) => (
            <div key={i} className="w-20 h-20 rounded-lg overflow-hidden border border-gray-200">
              <img src={URL.createObjectURL(f)} alt={`additional-${i}`} className="w-full h-full object-cover" />
            </div>
          ))}
          {!form.profile_photo && <p className="text-sm text-gray-400">No photos uploaded</p>}
        </div>
      </div>

      <label className="flex items-start gap-3 p-4 bg-[#FFF8F0] rounded-lg border border-[#FFD700]/30 cursor-pointer">
        <input type="checkbox" checked={agreedTerms} onChange={(e) => setAgreedTerms(e.target.checked)} className="accent-[#DC143C] w-5 h-5 mt-0.5" />
        <span className="text-sm text-gray-700">
          मी <a href="#" className="text-[#DC143C] underline">नियम आणि अटी</a> आणि <a href="#" className="text-[#DC143C] underline">गोपनीयता धोरण</a> वाचले आहेत आणि मान्य करतो.
          (I have read and agree to the Terms of Service and Privacy Policy.)
        </span>
      </label>
    </div>
  );

  const stepRenderers = [renderStep0, renderStep1, renderStep2, renderStep3, renderStep4, renderStep5, renderStep6, renderStep7];

  /* ================================================================ */
  /*  Render                                                            */
  /* ================================================================ */
  return (
    <div className="min-h-screen bg-[#FFF8F0] relative">
      {/* Mandala bg */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
        <div
          className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full"
          style={{
            background: 'repeating-radial-gradient(circle at center, transparent 0px, transparent 30px, rgba(139,0,0,0.2) 31px, transparent 32px)',
            animation: 'mandalaRotate 60s linear infinite',
          }}
        />
      </div>

      {/* Header */}
      <div className="bg-gradient-to-r from-[#8B0000] via-[#DC143C] to-[#8B0000] py-6 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-2xl font-['Noto_Sans_Devanagari'] font-bold text-[#FFD700]">विवाह आरंभ</Link>
          <div className="text-white/80 text-sm">
            Already registered? <Link to="/login" className="text-[#FFD700] font-semibold hover:underline">Login</Link>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 relative z-10">
        {/* Step indicator */}
        <div className="mb-8">
          <StepIndicator currentStep={step} />
        </div>

        {/* Form card */}
        <div className="bg-white rounded-2xl shadow-xl border border-[#FFD700]/20 p-6 md:p-8">
          {stepRenderers[step]()}

          {/* Navigation buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
            {step > 0 ? (
              <button
                type="button"
                onClick={prevStep}
                className="flex items-center gap-2 px-6 py-2.5 border-2 border-[#DC143C] text-[#DC143C] rounded-lg font-semibold hover:bg-[#DC143C] hover:text-white transition-all duration-200"
              >
                <FaArrowLeft /> मागे
              </button>
            ) : <div />}

            {step < 7 ? (
              <button
                type="button"
                onClick={nextStep}
                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#DC143C] to-[#8B0000] text-white rounded-lg font-semibold hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
              >
                पुढे <FaArrowRight />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-[#FFD700] to-[#B8860B] text-[#8B0000] rounded-lg font-bold text-lg hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-[#8B0000] border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <FaCheckCircle /> नोंदणी करा
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <p className="text-center text-gray-500 text-sm mt-6">
          Step {step + 1} of 8
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
